/**
 * Left Panel - Project/Team Navigation
 * 
 * Shows projects with their teams, recents, quick actions.
 * Controlled by header toggle button (same pattern as Composer/Chat toggles).
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useProjectStore } from '../../stores/projectStore';
import { ProjectGroup } from './ProjectGroup';
import { TeamListItem } from './TeamListItem';
import { QuickSearch } from './QuickSearch';
import { MoveTeamModal } from './MoveTeamModal';
import * as teamApi from '../../services/teamApi';

interface LeftPanelProps {
  visible: boolean;
  currentTeamId?: number;
  onSelectTeam: (teamId: number) => void;
  onCreateTeam: () => void;
  onCreateProject: () => void;
  onEditTeam: (teamId: number) => void;
  onEditProject: (projectId: number) => void;
  onShareTeam: (teamId: number) => void;
  onShareProject: (projectId: number) => void;
}

export const LeftPanel: React.FC<LeftPanelProps> = ({
  visible,
  currentTeamId,
  onSelectTeam,
  onCreateTeam,
  onCreateProject,
  onEditTeam,
  onEditProject,
  onShareTeam,
  onShareProject,
}) => {
  const { projects, unassignedTeams, loadProjects, loading } = useProjectStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [moveTeamId, setMoveTeamId] = useState<number | null>(null);
  const [moveTeamName, setMoveTeamName] = useState('');
  const [moveCurrentProjectId, setMoveCurrentProjectId] = useState<number>(0);
  const [archivedExpanded, setArchivedExpanded] = useState(false);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  // Cmd+K shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
      if (e.key === 'Escape') {
        setSearchOpen(false);
        setSearchQuery('');
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  // Auto-scroll when dragging near edges
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollIntervalRef = useRef<number | null>(null);
  const SCROLL_EDGE_PX = 40; // How close to edge to trigger scroll
  const SCROLL_SPEED = 6;    // Pixels per frame

  const handleDragOverScroll = useCallback((e: React.DragEvent) => {
    if (!scrollRef.current) return;
    const rect = scrollRef.current.getBoundingClientRect();
    const y = e.clientY;

    // Clear existing interval
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }

    // Near top edge: scroll up
    if (y < rect.top + SCROLL_EDGE_PX) {
      const tick = () => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop -= SCROLL_SPEED;
          scrollIntervalRef.current = requestAnimationFrame(tick);
        }
      };
      scrollIntervalRef.current = requestAnimationFrame(tick);
    }
    // Near bottom edge: scroll down
    else if (y > rect.bottom - SCROLL_EDGE_PX) {
      const tick = () => {
        if (scrollRef.current) {
          scrollRef.current.scrollTop += SCROLL_SPEED;
          scrollIntervalRef.current = requestAnimationFrame(tick);
        }
      };
      scrollIntervalRef.current = requestAnimationFrame(tick);
    }
  }, []);

  const stopAutoScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      cancelAnimationFrame(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  if (!visible) return null;

  // Split active vs archived: active projects with only non-archived teams; archived group at bottom
  const isProjectArchived = (p: { status?: string }) => (p.status || '') === 'archived';
  const isTeamArchived = (t: { status?: string }) => (t.status || '') === 'archived';
  const activeProjects = projects.filter(p => !isProjectArchived(p));
  const activeProjectsWithActiveTeams = activeProjects.map(p => ({
    ...p,
    teams: (p.teams || []).filter((t: any) => !isTeamArchived(t)),
  }));
  const activeUnassignedTeams = unassignedTeams.filter(t => !isTeamArchived(t));
  const archivedProjects = projects.filter(isProjectArchived);
  const archivedTeamsFromActiveProjects: { team: any; projectName: string; projectId: number }[] = [];
  projects.forEach(p => {
    if (isProjectArchived(p)) return;
    (p.teams || []).forEach((t: any) => {
      if (isTeamArchived(t)) archivedTeamsFromActiveProjects.push({ team: t, projectName: p.name, projectId: p.id });
    });
  });

  const hasArchived = archivedProjects.length > 0 || archivedTeamsFromActiveProjects.length > 0;

  const handleTeamDrop = async (teamId: number, targetProjectId: number) => {
    try {
      const { updateTeam } = await import('../../services/teamApi');
      await updateTeam(teamId, { project_id: targetProjectId } as any);
      loadProjects();
    } catch (error) {
      console.error('Failed to move team:', error);
    }
  };

  const handleArchiveTeam = async (teamId: number) => {
    if (window.confirm('Archive this team?')) {
      try {
        const { deleteTeam } = await import('../../services/teamApi');
        await deleteTeam(teamId);
        loadProjects();
      } catch (error) {
        console.error('Failed to archive team:', error);
      }
    }
  };

  const handleMoveToProject = (teamId: number) => {
    // Check unassigned teams first
    const unassigned = unassignedTeams.find((t: any) => t.id === teamId);
    if (unassigned) {
      setMoveTeamId(teamId);
      setMoveTeamName(unassigned.name || '');
      setMoveCurrentProjectId(0);
      return;
    }
    for (const p of projects) {
      const teams = (p as any).teams || [];
      const team = teams.find((t: any) => t.id === teamId);
      if (team) {
        setMoveTeamId(teamId);
        setMoveTeamName(team.name || '');
        setMoveCurrentProjectId(p.id);
        return;
      }
    }
    setMoveTeamId(teamId);
    setMoveTeamName('');
    setMoveCurrentProjectId(0);
  };

  const handleMoveTeamConfirm = async (projectId: number) => {
    if (moveTeamId == null) return;
    await teamApi.updateTeam(moveTeamId, { project_id: projectId } as any);
    loadProjects();
  };

  return (
    <div
      data-testid="left-panel"
      className="w-60 h-full flex flex-col bg-gray-900 border-r border-gray-800 overflow-hidden"
    >
      {/* Panel Header spacer */}
      <div className="py-1.5 bg-slate-900 border-b border-gray-800 flex-shrink-0" />

      {/* Quick Actions */}
      <div className="px-2.5 py-2.5 border-b border-gray-800 flex-shrink-0 space-y-2">
        {/* Search */}
        <button
          onClick={() => setSearchOpen(true)}
          className="w-full px-3 py-1.5 bg-gray-800 border border-gray-700 rounded-md text-gray-500 text-xs text-left hover:border-gray-600 transition-colors"
        >
          Search teams... Cmd+K
        </button>

        {/* Action buttons */}
        <div className="flex gap-1.5">
          <button
            onClick={onCreateTeam}
            className="flex-1 px-2 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium rounded-md transition-colors"
          >
            + New Team
          </button>
          <button
            onClick={onCreateProject}
            className="flex-1 px-2 py-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-300 text-xs font-medium rounded-md transition-colors"
          >
            + Project
          </button>
        </div>
      </div>

      {/* Scrollable content - auto-scrolls when dragging near edges */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-1 py-2 space-y-1"
        onDragOver={handleDragOverScroll}
        onDragLeave={stopAutoScroll}
        onDrop={stopAutoScroll}
        onDragEnd={stopAutoScroll}
      >
        {loading && projects.length === 0 ? (
          <div className="text-center text-gray-500 text-xs py-4">Loading...</div>
        ) : (
          <>
            {/* Projects */}
            <div className="px-1.5 py-1">
              <span className="text-gray-500 text-[10px] font-bold tracking-wider">PROJECTS</span>
            </div>

            {/* Unassigned Teams (no project) */}
            {activeUnassignedTeams.length > 0 && (
              <div className="mb-0.5">
                <div className="flex items-center gap-1 px-2 py-1.5 rounded">
                  <span className="text-xs font-semibold flex-1 truncate text-gray-400 italic">
                    Unassigned Teams
                  </span>
                  <span className="bg-gray-700 text-gray-400 text-[10px] px-1.5 py-0.5 rounded">
                    {activeUnassignedTeams.length}
                  </span>
                </div>
                <div className="ml-2">
                  {activeUnassignedTeams.map((team: any) => (
                    <TeamListItem
                      key={team.id}
                      team={team}
                      isSelected={currentTeamId === team.id}
                      onSelect={() => onSelectTeam(team.id)}
                      onEdit={() => onEditTeam(team.id)}
                      onShare={() => onShareTeam(team.id)}
                      onArchive={() => handleArchiveTeam(team.id)}
                      onMoveToProject={() => handleMoveToProject(team.id)}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Projects (active only) */}
            {activeProjectsWithActiveTeams.map(project => (
              <ProjectGroup
                key={project.id}
                project={project}
                currentTeamId={currentTeamId}
                onSelectTeam={onSelectTeam}
                onEditProject={() => onEditProject(project.id)}
                onShareProject={() => onShareProject(project.id)}
                onEditTeam={onEditTeam}
                onShareTeam={onShareTeam}
                onArchiveTeam={handleArchiveTeam}
                onMoveToProject={handleMoveToProject}
                onTeamDrop={handleTeamDrop}
                onCreateTeam={onCreateTeam}
              />
            ))}

            {/* Archived section (collapsed by default) */}
            {hasArchived && (
              <>
                <div className="px-1.5 py-1 mt-2">
                  <span className="text-gray-500 text-[10px] font-bold tracking-wider">ARCHIVED</span>
                </div>
                <div className="mb-0.5">
                  <div
                    data-testid="archived-section-toggle"
                    role="button"
                    tabIndex={0}
                    className="flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer hover:bg-gray-800/50 transition-colors"
                    onClick={() => setArchivedExpanded(!archivedExpanded)}
                    onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setArchivedExpanded(!archivedExpanded); } }}
                  >
                    <span className="text-gray-400 text-[10px] flex-shrink-0">
                      {archivedExpanded ? '▼' : '▶'}
                    </span>
                    <span className="text-gray-500 text-[11px]">
                      Archived ({archivedProjects.length + archivedTeamsFromActiveProjects.length})
                    </span>
                  </div>
                  {archivedExpanded && (
                    <div className="ml-2 mt-0.5 space-y-0.5">
                      {archivedProjects.map(project => (
                        <ProjectGroup
                          key={project.id}
                          project={project}
                          currentTeamId={currentTeamId}
                          onSelectTeam={onSelectTeam}
                          onEditProject={() => onEditProject(project.id)}
                          onShareProject={() => onShareProject(project.id)}
                          onEditTeam={onEditTeam}
                          onShareTeam={onShareTeam}
                          onArchiveTeam={handleArchiveTeam}
                          onMoveToProject={handleMoveToProject}
                          onTeamDrop={handleTeamDrop}
                          onCreateTeam={onCreateTeam}
                        />
                      ))}
                      {archivedTeamsFromActiveProjects.length > 0 && (
                        <div className="ml-2 mt-1">
                          <span className="text-gray-600 text-[10px] font-medium">Teams from active projects</span>
                          {archivedTeamsFromActiveProjects.map(({ team, projectName, projectId }) => (
                            <TeamListItem
                              key={team.id}
                              team={team}
                              isSelected={currentTeamId === team.id}
                              onSelect={() => onSelectTeam(team.id)}
                              onEdit={() => onEditTeam(team.id)}
                              onShare={() => onShareTeam(team.id)}
                              onArchive={() => handleArchiveTeam(team.id)}
                              onMoveToProject={() => handleMoveToProject(team.id)}
                              projectId={projectId}
                              subtitle={projectName}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Move Team Modal */}
      {moveTeamId !== null && (
        <MoveTeamModal
          teamId={moveTeamId}
          teamName={moveTeamName}
          currentProjectId={moveCurrentProjectId}
          projects={projects.map(p => ({ id: p.id, name: p.name }))}
          onClose={() => { setMoveTeamId(null); setMoveTeamName(''); setMoveCurrentProjectId(0); }}
          onMove={handleMoveTeamConfirm}
        />
      )}

      {/* Quick Search Modal */}
      {searchOpen && (
        <QuickSearch
          query={searchQuery}
          onQueryChange={setSearchQuery}
          onClose={() => { setSearchOpen(false); setSearchQuery(''); }}
          onSelectTeam={(teamId) => { onSelectTeam(teamId); setSearchOpen(false); setSearchQuery(''); }}
          projects={projects}
        />
      )}
    </div>
  );
};

export default LeftPanel;
